import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, LogOut } from 'lucide-react';
import { portalApi, portalLogin, session, errText } from '../components/portal/portalApi';
import { EntryForm } from '../components/portal/EntryForm';

const STATUS_STYLE = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-red-100 text-red-800' };

const usePortalHead = () => useEffect(() => {
  const add = (name, content) => { const m = document.createElement('meta'); m.name = name; m.content = content; document.head.appendChild(m); return m; };
  const tags = [add('robots', 'noindex, nofollow'), add('referrer', 'no-referrer')];
  const prevTitle = document.title;
  document.title = 'Godziny pracy – Trendhomes';
  return () => { tags.forEach((t) => t.remove()); document.title = prevTitle; };
}, []);

const PinLogin = ({ token, onLogged }) => {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true); setErr('');
    try { const d = await portalLogin(token, pin); session.set(token, { s: d.session }); onLogged(d.session); }
    catch (e) { setErr(errText(e, 'Nie udało się zalogować')); setPin(''); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="portal-login">
      <p className="text-lg text-slate-700">Wpisz PIN otrzymany z biura.</p>
      <input type="password" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => e.key === 'Enter' && pin.length >= 4 && submit()}
        className="w-full min-h-[60px] rounded-xl border border-slate-300 text-center text-3xl tracking-[0.5em] text-slate-900 bg-white" data-testid="portal-pin-input" />
      {err && <p className="rounded-xl bg-red-50 text-red-700 p-3" data-testid="portal-login-error">{err}</p>}
      <button type="button" disabled={busy || pin.length < 4} onClick={submit} data-testid="portal-login-btn"
        className="w-full min-h-[56px] rounded-xl bg-orange-500 text-white text-lg font-semibold disabled:opacity-50">{busy ? 'Sprawdzanie...' : 'Wejdź'}</button>
    </div>
  );
};

const MyEntries = ({ entries, onEdit, onDelete }) => (
  <div className="space-y-3" data-testid="portal-my-entries">
    <h2 className="text-xl font-bold text-slate-900">Moje wpisy (14 dni)</h2>
    {entries.length === 0 && <p className="text-slate-500">Brak wpisów.</p>}
    {entries.map((e) => (
      <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-1" data-testid={`portal-entry-${e.id}`}>
        <div className="flex justify-between items-center gap-2">
          <span className="font-semibold text-slate-900">{e.date} · {e.start}–{e.end}</span>
          <span className={`text-sm px-2 py-1 rounded-full ${STATUS_STYLE[e.status]}`} data-testid={`portal-entry-status-${e.id}`}>{e.status_label}</span>
        </div>
        <p className="text-slate-700">{e.hours.toLocaleString('pl-PL')} h · {e.place}</p>
        {e.reject_reason && <p className="text-sm text-red-700">Powód: {e.reject_reason}</p>}
        {e.editable && (
          <div className="flex gap-2 pt-2">
            <button type="button" className="flex-1 min-h-[44px] rounded-lg bg-slate-100 text-slate-800" onClick={() => onEdit(e)} data-testid={`portal-edit-${e.id}`}>Edytuj</button>
            <button type="button" className="flex-1 min-h-[44px] rounded-lg bg-red-50 text-red-700" onClick={() => onDelete(e)} data-testid={`portal-delete-${e.id}`}>Usuń</button>
          </div>
        )}
      </div>
    ))}
  </div>
);

const Portal = ({ sess, onLogout }) => {
  const api = useMemo(() => portalApi(sess), [sess]);
  const [me, setMe] = useState(null);
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => {
    api.me().then(setMe).catch((e) => e?.response?.status === 401 && onLogout());
    api.entries().then(setEntries).catch(() => {});
  }, [api, onLogout]);
  useEffect(() => { load(); }, [load]);
  if (!me) return <p className="text-slate-500">Ładowanie...</p>;
  const saved = () => { setMsg('Zapisano. Wpis czeka na akceptację biura.'); setEditing(null); setFormKey((k) => k + 1); load(); };
  const del = async (e) => { if (window.confirm('Usunąć ten wpis?')) { await api.remove(e.id); load(); } };
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <p className="text-lg text-slate-800" data-testid="portal-worker-name">Cześć, <strong>{me.name}</strong></p>
        <button type="button" onClick={async () => { await api.logout().catch(() => {}); onLogout(); }} className="flex items-center gap-1 text-slate-500" data-testid="portal-logout-btn">
          <LogOut className="w-5 h-5" />Wyloguj</button>
      </div>
      {msg && <p className="rounded-xl bg-emerald-50 text-emerald-800 p-3" data-testid="portal-success-msg">{msg}</p>}
      <section className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
        <h2 className="text-xl font-bold text-slate-900 mb-4">{editing ? 'Edytuj wpis' : 'Dodaj godziny'}</h2>
        <EntryForm key={editing ? editing.id : `new-${formKey}`} api={api} me={me} initial={editing} onSaved={saved} onCancel={editing ? () => setEditing(null) : null} />
      </section>
      <MyEntries entries={entries} onEdit={(e) => { setEditing(e); window.scrollTo({ top: 0, behavior: 'smooth' }); }} onDelete={del} />
    </div>
  );
};

export default function WorkerPortal() {
  const { token } = useParams();
  usePortalHead();
  const [sess, setSess] = useState(() => session.get(token)?.s || null);
  const logout = useCallback(() => { session.clear(); setSess(null); }, []);
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="worker-portal">
      <header className="bg-white border-b border-slate-200 px-4 py-4 flex items-center gap-2">
        <Clock className="w-7 h-7 text-orange-500" /><span className="text-xl font-bold">Trendhomes · Godziny pracy</span>
      </header>
      <main className="max-w-lg mx-auto p-4 pb-16">
        {sess ? <Portal sess={sess} onLogout={logout} /> : <PinLogin token={token} onLogged={setSess} />}
      </main>
    </div>
  );
}
